import { getScribeHttpClients } from '@heidi/api/client';
import type { components, ReviewReason } from '@heidi/schema/ml-scribe';

/**
 * The caller's ranked status (`null` with no record) and whether their saved
 * specialty needs the registry (`null` when no specialty resolves).
 */
export type HcpVerificationStatusResponse =
  components['schemas']['HcpVerificationStatusResponse'];
export type HcpVerificationRecord = components['schemas']['HcpVerificationResponse'];
export type ConfirmHcpRequest = components['schemas']['ConfirmHcpRequest'];
export type LookupHcpRequest = components['schemas']['LookupHcpRequest'];
export type HcpLookup = components['schemas']['HcpLookupResponse'];

/** Mirrors the backend ceilings so the picker refuses what the route would. */
export const HCP_REVIEW_MAX_FILES = 2;
export const HCP_REVIEW_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const HCP_REVIEW_JUSTIFICATION_MAX_LENGTH = 500;
export const HCP_REVIEW_ACCEPTED_FILE_TYPES: Record<string, readonly string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export interface RequestHcpReviewInput {
  reason: ReviewReason;
  /** Required by every reason that cites a registry outcome; ignored for `NO_IDENTIFIER`. */
  identifier?: string | null;
  reviewJustification?: string | null;
  files?: File[];
}

// Read at request time, not module scope: the registry is populated when the
// app builds its clients (`src/utils/scribeHTTP.ts`).
const client = () => getScribeHttpClients().scribeHttpV2;

/**
 * Reads the caller's clinician verification status.
 *
 * Cloud-only, US-only route: the backend gate answers 403 when the deployment,
 * the user's country or the server-side flag is out of scope, and a `null`
 * status for an eligible user who has not verified yet.
 *
 * Deliberately the status read, not `GET /hcp-verifications`: banners and
 * notification rows need the one ranked status alone, so the registry,
 * identifier, classifications and matched name never reach the client here.
 */
export const getHcpVerificationStatus = () =>
  client().get<HcpVerificationStatusResponse>('/hcp-verifications:status');

/**
 * Whether the caller's saved specialty needs the registry, as the review grant
 * reads it. The specialties list can disagree with the backend (it may be read
 * from another region), so a one-click grant is offered on this answer, never
 * the list's. `null` when no specialty resolves, which the grant treats as
 * needing it.
 */
export const getHcpSpecialtyRequirement = async (): Promise<boolean | null> => {
  const { data } = await getHcpVerificationStatus();
  return data.specialty_requires_verification ?? null;
};

/**
 * Previews what the registry holds for `identifier` without persisting
 * anything. The response never says whether another account already claimed
 * the identifier; only the confirm route answers that, with a 409.
 */
export const postHcpLookup = (body: LookupHcpRequest) =>
  client().post<HcpLookup>('/hcp-verifications:lookup', body);

/**
 * Attests that the registry record for `identifier` is the caller's. The
 * backend re-looks the identifier up and decides the status; the client sends
 * the identifier only — no name, no registry (the account's applies).
 */
export const postHcpVerification = (body: ConfirmHcpRequest) =>
  client().post<HcpVerificationRecord>('/hcp-verifications', body);

/**
 * Multipart body for `:request-review`. Each document is its own `files`
 * part; the backend reads the format from the bytes and drops the file name.
 */
export const buildHcpReviewFormData = ({
  reason,
  identifier,
  reviewJustification,
  files = [],
}: RequestHcpReviewInput): FormData => {
  const form = new FormData();
  form.append('reason', reason);
  if (identifier) form.append('identifier', identifier);
  if (reviewJustification) form.append('review_justification', reviewJustification);
  for (const file of files) form.append('files', file);
  return form;
};

/**
 * Raises a review case the user cannot resolve through self-service. Always
 * multipart, even with no files, because that is the route's encoding.
 */
export const postHcpReviewRequest = (form: FormData) =>
  client().post<HcpVerificationRecord>('/hcp-verifications:request-review', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
